import apiClient from '@/utils/request'
import {
  OnboardingSteps,
  IRequestPayload,
  IGeneralResponse,
  AdType,
  CPMCalculationType,
  MarketplaceStatus
} from '@/api/types'
import { nestedQueryFormatter } from '@/utils/query'

export interface IBecomePayload {
  token: string
  key: string
}

export interface IAdScanHitsBalance {
  hits: number
  original_hits: number
}

export interface IAdScanHitsType {
  system: IAdScanHitsBalance
  manual: IAdScanHitsBalance
}

export interface IUserOtpAttributes {
  required: boolean
  secret?: string
  url?: string
}

export interface IPaymentUnderReview {
  payment_system: string
  amount: number
  new_payment: boolean
}

export interface IUserOtpInfo {
  id: string
  type: 'user'
  attributes: {
    otp: IUserOtpAttributes
  }
}

export interface IUserAttributes {
  id: number
  name: string
  email: string
  roles: string
  balance: number
  with_valid_contact: boolean
  payments_under_review: IPaymentUnderReview[]
  with_valid_agreement: boolean
  agreement_creation_allowed: boolean
  skip_cpm_check: boolean
  with_valid_info: boolean
  staff: boolean
  rtb: boolean
  allow_smart_cpm: boolean
  use_conv: boolean
  stat_conversion_types: number[]
  available_cpm_types: Array<CPMCalculationType>
  referrer_code: string
  show_system_message: boolean
  system_message: string
  suid: string
  api_enabled: boolean
  api_token: string
  agreement_url: string
  with_widget: boolean
  widget_data: string
  verify_payment_data: boolean
  is_new_direct: boolean
  is_new_pub: boolean
  test_targeting_allowed: boolean
  extended_settings_allowed: boolean
  tour_id: number
  terms_accepted: boolean | null
  notifications: any
  with_confirmation_requests: boolean
  available_to_withdrawn: number
  marketplace_status: MarketplaceStatus
  enabled_features: string[]
  flat_deal_ad_type_ids: AdType[]
  marketplace_income: number
  service_fee_sum: number
  paid_amount: number
  replenishments_sum: number
  usage_tax_payed: boolean
  had_initial_talk: boolean
  initial_talk_requested: boolean
  onboarding_step: number
  onboarding_status: string
  connected_messengers: string[]
  flags: string[]
  partners_enabled: boolean
  deals_enabled: boolean
  ad_scan_hits: IAdScanHitsType
  next_usage_fee_date: string
  allowed_payments: string
  frozen: boolean
  referral_advert: number
  referral_publisher: number
  show_initial_survey_modal: boolean
  telegram_account: boolean | null
}

export interface IUser {
  id: string
  type: 'user'
  attributes: IUserAttributes
}

export interface IUserResponse extends IGeneralResponse {
  data: IUser
}

interface IUserOtpInfoResponse extends IGeneralResponse {
  data: IUserOtpInfo
}

export interface ITwoFaAttributes {
  password: string
  pin: string
}

export interface IPasswordAttributes {
  password: string
  password_confirmation: string
  current_password: string
}
export interface ILogin {
  email: string
  password: string
  otp_attempt: string
  remember_me: boolean
}

export interface IRegistration {
  email: string
  name: string
  password: string
  password_confirmation: string
  advert_type: string
  captcha?: string
  referral: string
  referer: string
  utms: any
}

export interface IResetPasswordAttributes {
  password: string
  password_confirmation: string
  token: string
}

export interface IBalanceItemAttributes {
  date: string
  balance: number
}

export interface IBalanceItem {
  attributes: IBalanceItemAttributes
}

export interface IBalanceMeta {
  total: number
  marketplace: number
  service_fee: number
  payed: number
  replenishments: number
}

interface IUserBalancesResponse extends IGeneralResponse {
  data: IBalanceItem[]
  meta: IBalanceMeta
}

export interface IStatistics {
  marketplace_income: number
  paid_amount: number
  replenishments_sum: number
  service_fee_sum: number
}
interface IUserStatisticsResponse extends IGeneralResponse {
  data: IStatistics
}

export const getUserInfo = async (
  withApiToken: boolean
): Promise<IUserResponse> => {
  const url = withApiToken ? '/user/api_token' : '/user'

  const response = await apiClient.get<IUserResponse>(url)
  return response.data
}

export const enableApiToken = async (): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>('/user/enable_api_token')
  return response.data
}

export const rotateApiToken = async (): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>('/user/rotate_api_token')
  return response.data
}

export const disableApiToken = async (): Promise<IUserResponse> => {
  const response = await apiClient.delete<IUserResponse>(
    '/user/disable_api_token'
  )
  return response.data
}

export const changePassword = async (
  payload: IPasswordAttributes
): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/change_password',
    payload
  )
  return response.data
}

export const getUserOtpInfo = async (): Promise<IUserOtpInfoResponse> => {
  const response = await apiClient.get<IUserOtpInfoResponse>('/user/otp_info')
  return response.data
}

export const enableTwoFa = async (
  payload: ITwoFaAttributes
): Promise<IUserOtpInfoResponse> => {
  const response = await apiClient.put<IUserOtpInfoResponse>(
    '/user/enable_two_fa',
    payload
  )
  return response.data
}

export const disableTwoFa = async (
  payload: ITwoFaAttributes
): Promise<IUserOtpInfoResponse> => {
  const response = await apiClient.put<IUserOtpInfoResponse>(
    '/user/disable_two_fa',
    payload
  )
  return response.data
}

export const logout = async (): Promise<IUserResponse> => {
  const response = await apiClient.delete<IUserResponse>('/sessions')
  return response.data
}

export const login = async (payload: ILogin): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>('/sessions', payload)
  return response.data
}

export const registration = async (
  payload: IRegistration
): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/login_requests',
    payload
  )
  return response.data
}

export const getBack = async (): Promise<IUserResponse> => {
  const response = await apiClient.delete<IUserResponse>('/sessions/get_back')
  return response.data
}

export const resetPassword = async (
  payload: IResetPasswordAttributes
): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/reset_password',
    payload
  )
  return response.data
}

export const confirmAccount = async (token: string): Promise<IUserResponse> => {
  const payload = { token: token }
  const response = await apiClient.post<IUserResponse>(
    '/user/confirm_registration',
    payload
  )
  return response.data
}

export const sendConfirmation = async (
  email: string
): Promise<IUserResponse> => {
  const payload = { email: email }
  const response = await apiClient.post<IUserResponse>(
    '/user/send_confirmation_letter',
    payload
  )
  return response.data
}

export const setInitialTags = async (
  tags: string[]
): Promise<IUserResponse> => {
  const payload = { tags: tags }
  const response = await apiClient.post<IUserResponse>(
    '/user/set_initial_tag',
    payload
  )

  return response.data
}

export const unsubscribe = async (token: string): Promise<IUserResponse> => {
  const payload = { token: token }
  const response = await apiClient.post<IUserResponse>(
    '/user/unsubscribe',
    payload
  )
  return response.data
}

export const checkSubscription = async (
  token: string
): Promise<IUserResponse> => {
  const response = await apiClient.get<IUserResponse>(
    '/user/check_subscription',
    { params: { token } }
  )
  return response.data
}

export const sendRecovery = async (email: string): Promise<IUserResponse> => {
  const payload = { email: email }
  const response = await apiClient.post<IUserResponse>(
    '/user/send_recovery_letter',
    payload
  )
  return response.data
}

export const socialAuth = async (
  provider: string,
  payload: any,
  referral: any,
  referer: any,
  utms: any,
  type?: any
): Promise<IUserResponse> => {
  const params = {
    ...payload,
    referral,
    referer,
    ...nestedQueryFormatter('utms', utms)
  }
  if (type) {
    params['advert_type'] = type
  }
  const response = await apiClient.get<IUserResponse>(`/sessions/${provider}`, {
    params: params
  })
  return response.data
}

export const setMessageAsRead = async (): Promise<IUserResponse> => {
  const payload = { show_system_message: false }
  const response = await apiClient.put<IUserResponse>(
    '/user/set_message_as_read',
    payload
  )
  return response.data
}

export const acceptCurrentTerms = async (): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/accept_current_terms'
  )
  return response.data
}

export const saveNotifications = async (
  payload: any
): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/save_notifications',
    payload
  )
  return response.data
}

export const getBalances = async (
  period: string
): Promise<IUserBalancesResponse> => {
  const response = await apiClient.get<IUserBalancesResponse>(
    `/user/balances?period=${period}`
  )
  return response.data
}

export const getBalancesRange = async (
  range: string
): Promise<IUserBalancesResponse> => {
  const response = await apiClient.get<IUserBalancesResponse>(
    `/user/balances?range=${range}`
  )
  return response.data
}

export const getStatistics = async (
  period: string
): Promise<IUserStatisticsResponse> => {
  const response = await apiClient.get<IUserStatisticsResponse>(
    `/user/statistics?period=${period}`
  )
  return response.data
}

export const requestMarketplace = async (): Promise<IUserResponse> => {
  // The marketplace panel reports the failure itself, with a fallback that names what
  // the user was doing — so the interceptor stays out of it rather than adding a second
  // toast saying the same thing.
  const response = await apiClient.post<IUserResponse>(
    '/user/request_marketplace',
    undefined,
    { skipGlobalErrorHandler: true }
  )
  return response.data
}

export const requestCall = async (channel: string): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>('/user/request_call', {
    channel
  })
  return response.data
}

export const getTelegramLink = async (): Promise<string> => {
  const response = await apiClient.get('/user/telegram_link')
  return response.data.link
}

export const disconnectTelegram = async (): Promise<IUserResponse> => {
  const response = await apiClient.delete<IUserResponse>(
    '/user/telegram_account'
  )
  return response.data
}

export const becomeUser = async (
  payload: IBecomePayload
): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/sessions/become',
    payload
  )
  return response.data
}

export const toggleMarket = async (status: boolean): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/toogle_marketplace',
    { status }
  )
  return response.data
}

export const togglePaymentsStatus = async (): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/toogle_payments_status'
  )
  return response.data
}

export const toggleFrozenStatus = async (): Promise<IUserResponse> => {
  const response = await apiClient.post<IUserResponse>(
    '/user/toogle_frozen_status'
  )
  return response.data
}
